import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import { Customer } from '../modals/CustomerDetailModal';
import CustomerCard from './CustomerCard';
import { normalizePhoneForMatch } from '@/lib/validator';

interface DuplicateGroupCardProps {
    group: Customer[];
    index: number;
    isSelected: boolean;
    isExpanded: boolean;
    onSelect: () => void;
    onToggleExpand: () => void;
}

const DuplicateGroupCard: React.FC<DuplicateGroupCardProps> = ({
    group,
    index,
    isSelected,
    isExpanded,
    onSelect,
    onToggleExpand
}) => {
    const { t } = useTranslation();

    // In intestazione vanno solo i contatti davvero condivisi da almeno due profili:
    // elencare tutti i telefoni del gruppo faceva passare per "in comune" numeri diversi.
    const phones = useMemo(() => {
        const byKey = new Map<string, string>();
        const shared = new Set<string>();
        group.forEach(c => {
            const key = c.phone ? normalizePhoneForMatch(c.phone) : null;
            if (!key) return;
            if (byKey.has(key)) shared.add(key);
            else byKey.set(key, c.phone!.trim());
        });
        return Array.from(shared).map(key => byKey.get(key)!);
    }, [group]);

    const emails = useMemo(() => {
        const byKey = new Map<string, string>();
        const shared = new Set<string>();
        group.forEach(c => {
            const key = c.email?.trim().toLowerCase();
            if (!key) return;
            if (byKey.has(key)) shared.add(key);
            else byKey.set(key, c.email!.trim());
        });
        return Array.from(shared).map(key => byKey.get(key)!);
    }, [group]);

    return (
        <div
            className={`bg-white rounded-2xl border-2 overflow-hidden transition-all duration-300 ${
                isSelected ? 'border-orange-500' : 'border-gray-200'
            }`}
        >
            {/* Accordion Header */}
            <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={onSelect}
            >
                <div className="flex items-center gap-4">
                    {/* Add shrink-0 to prevent the checkbox from stretching/shrinking on mobile */}
                    <div
                        className={`w-6 h-6 shrink-0 rounded-md flex items-center justify-center border-2 transition-colors ${
                            isSelected ? 'bg-orange-500 border-orange-500' : 'border-gray-300'
                        }`}
                    >
                        {isSelected && <Check size={14} className="text-white" />}
                    </div>
                    <div>
                        <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                            {t('customers.duplicates.group')} {index + 1}
                            <span className="text-sm font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                                {group.length} {t('customers.duplicates.profiles')}
                            </span>
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                            {phones.length > 0 && (
                                <span>
                                    {t('customers.duplicates.common_phone')}: <strong>{phones.join(', ')}</strong>
                                </span>
                            )}
                            {phones.length > 0 && emails.length > 0 && <span className="mx-2">•</span>}
                            {emails.length > 0 && (
                                <span>
                                    {t('customers.duplicates.common_email')}: <strong>{emails.join(', ')}</strong>
                                </span>
                            )}
                        </p>
                    </div>
                </div>

                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggleExpand();
                    }}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                >
                    {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
            </div>

            {/* Accordion Content */}
            {isExpanded && (
                <div className="p-6 bg-gray-50/50 border-t border-gray-100">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {group.map((customer) => (
                            <div key={customer.id} className="pointer-events-none">
                                {/* Usiamo pointer-events-none per non far cliccare i pulsanti all'interno in questa vista */}
                                <CustomerCard customer={customer} onClick={() => {}} />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DuplicateGroupCard;
